import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtDate, money } from "@/lib/constants";
import { toast } from "sonner";
import { MagnifyingGlass, X, DotsThreeOutline, Pause, Play, Copy, Archive, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";
import BuyerCombobox from "@/components/shared/BuyerCombobox";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_SORT = "name_asc";
const DEFAULT_STATUS = "all";
const PAGE_SIZE = 25;
const SORT_OPTIONS = [
  { value: "name_asc",     label: "Name A → Z" },
  { value: "name_desc",    label: "Name Z → A" },
  { value: "created_desc", label: "Newest" },
  { value: "created_asc",  label: "Oldest" },
];
const LIFECYCLE_STATUS_OPTIONS = [
  { value: "all",      label: "Status: All" },
  { value: "active",   label: "Active" },
  { value: "paused",   label: "Paused" },
  { value: "archived", label: "Archived" },
];

export default function Campaigns() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: PAGE_SIZE, has_next: false });
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: "", client_id: "", client_name: "", price: "", return_window_days: 30,
    accept_hours: "", is_active: true,
  });

  // Iter-61.4.2 — search / sort / filter / pagination toolbar.
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [buyerFilter, setBuyerFilter] = useState(null);   // {id, name} or null
  const [page, setPage] = useState(1);

  const filtersActive =
    q !== "" || sort !== DEFAULT_SORT || status !== DEFAULT_STATUS || !!buyerFilter?.id;

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (buyerFilter?.id) params.set("client_id", buyerFilter.id);
    if (status === "active") params.set("active_only", "true");
    // Iter-63.1: include archived rows only when the admin asks for
    // them — default list still hides archived campaigns.
    if (status === "archived" || status === "all") {
      params.set("include_archived", "true");
    }
    params.set("sort", sort);
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));
    const { data } = await api.get(`/campaigns?${params.toString()}`);
    let rows = data.campaigns || [];
    // active_only=true filters server-side; the paused / archived views
    // are refined client-side over the returned window.
    if (status === "paused") rows = rows.filter((c) => !c.is_active && !c.is_archived);
    if (status === "archived") rows = rows.filter((c) => c.is_archived);
    if (status === "all") { /* no filter */ }
    setRows(rows);
    if (data.pagination) setPagination(data.pagination);
  }, [q, sort, status, buyerFilter, page]);

  useEffect(() => { setPage(1); }, [q, sort, status, buyerFilter]);
  useEffect(() => { load(); }, [load]);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQ(qInput.trim()), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [qInput]);

  const clearFilters = () => {
    setQInput(""); setQ("");
    setSort(DEFAULT_SORT); setStatus(DEFAULT_STATUS);
    setBuyerFilter(null); setPage(1);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Select a client for this campaign");
    try {
      await api.post("/campaigns", {
        name: form.name, client_id: form.client_id,
        price: form.price === "" ? null : Number(form.price),
        return_window_days: Number(form.return_window_days),
        accept_hours: form.accept_hours,
        is_active: form.is_active,
      });
      toast.success("Campaign created");
      setShowNew(false);
      setForm({ name: "", client_id: "", client_name: "", price: "",
                return_window_days: 30, accept_hours: "", is_active: true });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const total = pagination.total || 0;
  const visible = rows.length;
  const start = total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const end   = (pagination.page - 1) * pagination.page_size + visible;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="label-mono">Verticals</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Campaigns</h1>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="toggle-new-campaign"
          className="self-start md:self-auto bg-black text-white px-3 py-2 text-xs uppercase tracking-widest border border-black hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
          {showNew ? "Close" : "New campaign"}
        </button>
      </div>

      <div className="brut-card p-3 mb-3 flex flex-col md:flex-row md:items-center gap-2 md:flex-wrap"
           data-testid="campaigns-toolbar">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input type="text" value={qInput} onChange={(e) => setQInput(e.target.value)}
            placeholder="Search campaign name or public ID"
            className="w-full border border-black pl-7 pr-2 py-1.5 text-sm"
            data-testid="campaigns-search-input" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-56">
            <BuyerCombobox value={buyerFilter} onChange={setBuyerFilter}
              includeAll allLabel="Buyer: All" placeholder="Buyer: All"
              testIdPrefix="campaigns-buyer-filter" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="campaigns-status-filter"
            className="border border-black px-2 py-1.5 text-sm">
            {LIFECYCLE_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
            data-testid="campaigns-sort-select"
            className="border border-black px-2 py-1.5 text-sm">
            {SORT_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
          {filtersActive && (
            <button onClick={clearFilters} data-testid="campaigns-clear-filters"
              className="border border-black px-2 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1">
              <X size={12} /> Clear filters
            </button>
          )}
        </div>
      </div>

      {showNew && (
        <form onSubmit={submit} className="brut-card p-5 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="new-camp-form">
          <div>
            <label className="label-mono">Name*</label>
            <input required data-testid="camp-name" className="w-full border border-black px-3 py-2 mt-1"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label-mono">Client*</label>
            <BuyerCombobox
              value={form.client_id ? { id: form.client_id, name: form.client_name } : null}
              onChange={(v) => setForm((prev) => ({ ...prev, client_id: v?.id || "", client_name: v?.name || "" }))}
              placeholder="— select a buyer —"
              testIdPrefix="camp-client"
            />
          </div>
          <div>
            <label className="label-mono">Price ($/lead)</label>
            <input type="number" step="0.01" className="w-full border border-black px-3 py-2 mt-1"
              value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          </div>
          <div>
            <label className="label-mono">Return window (days)</label>
            <input type="number" className="w-full border border-black px-3 py-2 mt-1"
              value={form.return_window_days} onChange={(e) => setForm({ ...form, return_window_days: e.target.value })} />
          </div>
          <div className="col-span-1 md:col-span-2">
            <label className="label-mono">Accept hours</label>
            <input placeholder="e.g. Mon-Fri 09:00-18:00 PT" className="w-full border border-black px-3 py-2 mt-1"
              value={form.accept_hours} onChange={(e) => setForm({ ...form, accept_hours: e.target.value })} />
          </div>
          <div className="col-span-1 md:col-span-2 flex justify-end">
            <button type="submit" data-testid="camp-submit"
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold">Create</button>
          </div>
        </form>
      )}

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[820px]">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-4 text-left">Name</th>
              <th className="py-2 px-4 text-left">Public ID</th>
              <th className="py-2 px-4 text-left">Client</th>
              <th className="py-2 px-4 text-left">Price</th>
              <th className="py-2 px-4 text-left">Return window</th>
              <th className="py-2 px-4 text-left">Status</th>
              <th className="py-2 px-4 text-left">Created</th>
              <th className="py-2 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <CampaignRow key={c.id} campaign={c} onChanged={load} />
            ))}
            {!rows.length && <tr><td colSpan={8} className="py-6 text-center label-mono">No campaigns</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs font-mono uppercase tracking-widest text-zinc-700">
        <div data-testid="campaigns-result-count">
          {total === 0
            ? "No campaigns match"
            : `Showing ${start}${start !== end ? `–${end}` : ""} of ${total} campaigns`}
        </div>
        <div className="flex items-center gap-2" data-testid="campaigns-pagination">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
            data-testid="campaigns-page-prev"
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Prev</button>
          <span>Page {pagination.page || 1}</span>
          <button onClick={() => setPage(page + 1)} disabled={!pagination.has_next}
            data-testid="campaigns-page-next"
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Next</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Iter-63.1 — One row of the Campaigns table with edit + lifecycle
 * actions. Editing is inline (name / price / return_window / accept_hours).
 * Every destructive action uses the existing guarded backend routes:
 *
 *   pause / resume / clone / archive / restore / delete-preview / DELETE
 */
function CampaignRow({ campaign, onChanged }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({
    name: campaign.name || "",
    price: campaign.price ?? "",
    return_window_days: campaign.return_window_days ?? 30,
    accept_hours: campaign.accept_hours || "",
  });
  const [saving, setSaving] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [deletePreview, setDeletePreview] = React.useState(null);
  const [confirmToken, setConfirmToken] = React.useState("");

  const statusLabel = campaign.is_archived
    ? "archived"
    : (campaign.is_active ? "active" : "paused");
  const statusCls = campaign.is_archived
    ? "bg-zinc-300 border-zinc-700 text-zinc-800"
    : (campaign.is_active
       ? "bg-emerald-600 border-emerald-700 text-white"
       : "bg-amber-100 border-amber-700 text-amber-900");

  const saveEdit = async () => {
    setSaving(true);
    try {
      const patch = {
        name: form.name.trim(),
        price: form.price === "" ? null : Number(form.price),
        return_window_days: Number(form.return_window_days),
        accept_hours: form.accept_hours,
      };
      await api.patch(`/campaigns/${campaign.id}`, patch);
      toast.success("Campaign updated");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (action, extra = {}) => {
    setBusy(true);
    try {
      if (action === "pause")   await api.post(`/campaigns/${campaign.id}/pause`);
      if (action === "resume")  await api.post(`/campaigns/${campaign.id}/resume`);
      if (action === "clone")   await api.post(`/campaigns/${campaign.id}/clone`, extra);
      if (action === "archive") await api.post(`/campaigns/${campaign.id}/archive`, extra);
      if (action === "restore") await api.post(`/campaigns/${campaign.id}/restore`);
      toast.success(`Campaign ${action}d`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  const openDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/campaigns/${campaign.id}/delete-preview`);
      setDeletePreview(data);
      setConfirmToken("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };
  const submitDelete = async () => {
    if (!deletePreview) return;
    setBusy(true);
    try {
      await api.delete(`/campaigns/${campaign.id}`, {
        data: { confirm: confirmToken },
      });
      toast.success("Campaign deleted");
      setDeletePreview(null);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr className={`border-b border-zinc-200 hover:bg-zinc-100 ${campaign.is_archived ? "opacity-70" : ""}`}
          data-testid={`camp-row-${campaign.id}`}>
        <td className="py-2 px-4 font-semibold">
          {editing ? (
            <input className="border border-black px-2 py-1 w-full"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid={`camp-edit-name-${campaign.id}`} />
          ) : campaign.name}
        </td>
        <td className="py-2 px-4">{campaign.public_id}</td>
        <td className="py-2 px-4">{campaign.client_name || campaign.client_id?.slice(0, 8) || "—"}</td>
        <td className="py-2 px-4">
          {editing ? (
            <input type="number" step="0.01" className="border border-black px-2 py-1 w-24"
              value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
              data-testid={`camp-edit-price-${campaign.id}`} />
          ) : money(campaign.price)}
        </td>
        <td className="py-2 px-4">
          {editing ? (
            <input type="number" className="border border-black px-2 py-1 w-20"
              value={form.return_window_days}
              onChange={(e) => setForm({ ...form, return_window_days: e.target.value })}
              data-testid={`camp-edit-window-${campaign.id}`} />
          ) : `${campaign.return_window_days}d`}
        </td>
        <td className="py-2 px-4">
          <span className={`inline-block text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${statusCls}`}
                data-testid={`camp-status-${campaign.id}`}>
            {statusLabel}
          </span>
        </td>
        <td className="py-2 px-4 text-[11px]">{fmtDate(campaign.created_at)}</td>
        <td className="py-2 px-4 text-right whitespace-nowrap">
          {editing ? (
            <div className="inline-flex gap-1">
              <button onClick={saveEdit} disabled={saving}
                data-testid={`camp-save-${campaign.id}`}
                className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest bg-black text-white disabled:opacity-40">
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setEditing(false)} disabled={saving}
                data-testid={`camp-cancel-${campaign.id}`}
                className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest">
                Cancel
              </button>
            </div>
          ) : (
            <div className="inline-flex flex-wrap gap-1 justify-end">
              <button onClick={() => setEditing(true)} disabled={busy || campaign.is_archived}
                data-testid={`camp-edit-${campaign.id}`}
                className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40">
                Edit
              </button>
              {!campaign.is_archived && campaign.is_active && (
                <button onClick={() => doAction("pause")} disabled={busy}
                  data-testid={`camp-pause-${campaign.id}`}
                  className="border border-amber-700 text-amber-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-amber-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
                  <Pause size={10} weight="bold" /> Pause
                </button>
              )}
              {!campaign.is_archived && !campaign.is_active && (
                <button onClick={() => doAction("resume")} disabled={busy}
                  data-testid={`camp-resume-${campaign.id}`}
                  className="border border-emerald-700 text-emerald-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
                  <Play size={10} weight="bold" /> Resume
                </button>
              )}
              <button onClick={() => doAction("clone")} disabled={busy}
                data-testid={`camp-clone-${campaign.id}`}
                className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
                <Copy size={10} weight="bold" /> Clone
              </button>
              {!campaign.is_archived && (
                <button onClick={() => doAction("archive")} disabled={busy}
                  data-testid={`camp-archive-${campaign.id}`}
                  className="border border-zinc-700 text-zinc-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-zinc-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
                  <Archive size={10} weight="bold" /> Archive
                </button>
              )}
              {campaign.is_archived && (
                <button onClick={() => doAction("restore")} disabled={busy}
                  data-testid={`camp-restore-${campaign.id}`}
                  className="border border-emerald-700 text-emerald-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
                  <ArrowCounterClockwise size={10} weight="bold" /> Restore
                </button>
              )}
              {isSuperAdmin && (
                <button onClick={openDelete} disabled={busy}
                  data-testid={`camp-delete-${campaign.id}`}
                  className="border border-red-700 text-red-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
                  <Trash size={10} weight="bold" /> Delete
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
      {deletePreview && (
        <tr>
          <td colSpan={8} className="p-0">
            <div className="bg-red-50 border-t-2 border-red-700 border-b-2 p-4"
                 data-testid={`camp-delete-preview-${campaign.id}`}>
              <div className="font-chivo font-black uppercase tracking-tighter text-red-900 mb-2">
                Delete campaign — dependency check
              </div>
              <div className="text-xs mb-3">
                <div>Leads: <b>{deletePreview.dependencies?.leads ?? 0}</b></div>
                <div>Delivery attempts: <b>{deletePreview.dependencies?.delivery_attempts ?? 0}</b></div>
                <div>Invoice lines: <b>{deletePreview.dependencies?.invoice_lines ?? 0}</b></div>
                <div>Destinations: <b>{deletePreview.dependencies?.destinations ?? 0}</b></div>
              </div>
              {!deletePreview.safe_to_delete ? (
                <div className="text-xs text-red-900 mb-2" data-testid={`camp-delete-forbidden-${campaign.id}`}>
                  <b>Cannot delete</b> — this campaign has downstream history. Archive it
                  instead to preserve billing / audit records.
                </div>
              ) : (
                <div className="mb-2 flex flex-wrap gap-2 items-center">
                  <label className="label-mono">Confirm token</label>
                  <input type="text"
                    value={confirmToken}
                    onChange={(e) => setConfirmToken(e.target.value)}
                    placeholder={deletePreview.confirm_token}
                    className="border border-black px-2 py-1 text-xs font-mono w-64"
                    data-testid={`camp-delete-confirm-input-${campaign.id}`} />
                  <span className="text-[10px] text-zinc-600">
                    Type exactly: <code className="font-mono">{deletePreview.confirm_token}</code>
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setDeletePreview(null)}
                  data-testid={`camp-delete-cancel-${campaign.id}`}
                  className="border border-black px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                  Cancel
                </button>
                {deletePreview.safe_to_delete && (
                  <button onClick={submitDelete}
                    disabled={busy || confirmToken !== deletePreview.confirm_token}
                    data-testid={`camp-delete-confirm-${campaign.id}`}
                    className="border border-red-700 bg-red-700 text-white px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-red-800 disabled:opacity-40">
                    Delete permanently
                  </button>
                )}
                {!deletePreview.safe_to_delete && (
                  <button onClick={async () => { await doAction("archive", {}); setDeletePreview(null); }}
                    disabled={busy}
                    data-testid={`camp-delete-archive-instead-${campaign.id}`}
                    className="border border-amber-700 bg-amber-700 text-white px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-amber-800 disabled:opacity-40">
                    Archive instead
                  </button>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
